import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

/**
 * useVisitorSession — anonymous visitor session tracker.
 *
 * Mounts once at the App root:
 *   - Generates a stable session_id in localStorage (persists 30 minutes of idle)
 *   - POSTs /analytics/session/start on first paint
 *   - Sends a heartbeat every 20 s with elapsed duration
 *   - Sends /session/end via navigator.sendBeacon on `beforeunload`
 *
 * Backend uses the duration to compute avg-time-on-site AND to fire a
 * long-visit WhatsApp/email nudge once the visitor crosses 3 minutes AND
 * has identified themselves via a lead popup or booking form.
 */
export default function useVisitorSession() {
    const started = useRef(false);
    const startedAt = useRef(Date.now());

    useEffect(() => {
        if (started.current) return;
        started.current = true;

        // Reuse an existing session_id if the visitor was here in the last 30 min
        const KEY = "smart_visitor_session";
        const now = Date.now();
        let cached = null;
        try { cached = JSON.parse(localStorage.getItem(KEY) || "null"); } catch { /* noop */ }
        if (cached && now - cached.ts < 30 * 60 * 1000) {
            startedAt.current = cached.startedAt || now;
        } else {
            cached = {
                id: crypto.randomUUID?.() || `${now}-${Math.random().toString(36).slice(2)}`,
                startedAt: now,
                ts: now,
            };
            localStorage.setItem(KEY, JSON.stringify(cached));
            startedAt.current = now;
        }
        const sid = cached.id;

        // Fire-and-forget start (never blocks paint or fails loudly)
        api.post("/analytics/session/start", {
            session_id: sid,
            path: window.location.pathname,
            referrer: document.referrer || null,
        }).catch(() => { /* silent */ });

        const durSecs = () => Math.floor((Date.now() - startedAt.current) / 1000);

        const beat = () => {
            api.post("/analytics/session/heartbeat", {
                session_id: sid,
                duration_seconds: durSecs(),
                path: window.location.pathname,
            }).catch(() => { /* silent */ });
            // Extend the localStorage TTL on every beat
            try {
                localStorage.setItem(KEY, JSON.stringify({ ...cached, ts: Date.now() }));
            } catch { /* noop */ }
        };
        const interval = setInterval(beat, 20_000);

        const end = () => {
            const payload = JSON.stringify({ session_id: sid, duration_seconds: durSecs() });
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/session/end`;
            try {
                if (navigator.sendBeacon) {
                    navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
                } else {
                    // last-resort synchronous fetch (may be cancelled but worth trying)
                    fetch(url, { method: "POST", body: payload, headers: { "Content-Type": "application/json" }, keepalive: true });
                }
            } catch { /* silent */ }
        };
        window.addEventListener("beforeunload", end);
        // Visibility change → treat hidden as pause; fire an early beat when it comes back
        const onVis = () => { if (!document.hidden) beat(); };
        document.addEventListener("visibilitychange", onVis);

        // Expose an identify() helper on the window for lead popups + auth pages
        // to call once the visitor gives us contact info.
        window.__smartIdentifyVisitor = (contact) => {
            if (!contact) return;
            api.post("/analytics/session/identify", { session_id: sid, ...contact })
                .catch(() => { /* silent */ });
        };

        return () => {
            clearInterval(interval);
            window.removeEventListener("beforeunload", end);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);
}
